import {
  CONSENT_CATEGORIES,
  CONSENT_STATE_SCHEMA_VERSION,
  isConsentCategory,
} from './constants.js';
import type { ConsentChoices, ConsentState } from './types.js';

const CONSENT_ID_PATTERN = /^gcs_[0-9A-HJKMNP-TV-Z]{26}$/;
const POLICY_VERSION_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/;
const UTC_TIMESTAMP_PATTERN =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?Z$/;

export function isCanonicalConsentId(value: unknown): value is string {
  return typeof value === 'string' && CONSENT_ID_PATTERN.test(value);
}

export function isCanonicalPolicyVersion(value: unknown): value is string {
  return typeof value === 'string' && POLICY_VERSION_PATTERN.test(value);
}

export function isCanonicalUtcTimestamp(value: unknown): value is string {
  if (typeof value !== 'string' || !UTC_TIMESTAMP_PATTERN.test(value)) {
    return false;
  }

  const parsed = Date.parse(value);
  if (Number.isNaN(parsed)) {
    return false;
  }

  const normalizedInput = value.includes('.')
    ? value.replace(
        /\.(\d{1,3})Z$/,
        (_match, fraction: string) => `.${fraction.padEnd(3, '0')}Z`,
      )
    : value.replace(/Z$/, '.000Z');

  return new Date(parsed).toISOString() === normalizedInput;
}

export function hasCanonicalConsentChoices(
  value: unknown,
): value is ConsentChoices {
  if (!isRecord(value)) {
    return false;
  }

  const keys = Object.keys(value);

  return (
    keys.length === CONSENT_CATEGORIES.length &&
    keys.every((key) => isConsentCategory(key)) &&
    CONSENT_CATEGORIES.every(
      (category) => typeof value[category] === 'boolean',
    ) &&
    value.necessary === true
  );
}

export function isConsentState(value: unknown): value is ConsentState {
  if (!isRecord(value)) {
    return false;
  }

  const expectedKeys = [
    'id',
    'schemaVersion',
    'revision',
    'policyVersion',
    'configurationVersion',
    'categories',
    'updatedAt',
  ];

  return (
    Object.keys(value).length === expectedKeys.length &&
    expectedKeys.every((key) => hasOwn(value, key)) &&
    isCanonicalConsentId(value.id) &&
    value.schemaVersion === CONSENT_STATE_SCHEMA_VERSION &&
    Number.isSafeInteger(value.revision) &&
    (value.revision as number) >= 1 &&
    isCanonicalPolicyVersion(value.policyVersion) &&
    Number.isSafeInteger(value.configurationVersion) &&
    (value.configurationVersion as number) >= 1 &&
    hasCanonicalConsentChoices(value.categories) &&
    isCanonicalUtcTimestamp(value.updatedAt)
  );
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function hasOwn(value: object, key: PropertyKey): boolean {
  return Object.prototype.hasOwnProperty.call(value, key);
}
